/**
 * Board writes settle in the background: the UI patches optimistically (a status chip, a dragged
 * card) and the request finishes later. An in-app route change is safe — nothing cancels the XHR —
 * but a reload or a closed tab aborts it, and the change the user already saw is gone after the
 * reload. So while any board write is in flight the page asks the browser to confirm leaving,
 * and stops asking the moment the last one settles, whether it succeeded or failed.
 */

let pendingWriteCount: number = 0;

const confirmLeavingWithPendingWrites = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
  // Chrome only shows the prompt when returnValue is set as well.
  event.returnValue = "";
};

export function hasPendingBoardWrites(): boolean {
  return pendingWriteCount > 0;
}

/** Runs one board write with the unload guard held until it settles. */
export async function trackBoardWrite<T>(write: () => Promise<T>): Promise<T> {
  if (pendingWriteCount === 0) window.addEventListener("beforeunload", confirmLeavingWithPendingWrites);
  pendingWriteCount += 1;
  try {
    return await write();
  } finally {
    pendingWriteCount -= 1;
    if (pendingWriteCount === 0) window.removeEventListener("beforeunload", confirmLeavingWithPendingWrites);
  }
}
